import React, { useEffect, useRef, useState } from 'react';

import { Button } from '../../../components/Button/Button';
import { CloseIcon } from '../../../components/icons/Close';
import { useCart } from '../../../contexts/CartContext';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { resolveCjLink } from '../cjLinks';
import { isShoppable } from '../purchaseMethods';
import type {
    ProductInfo,
    PurchaseAvailability,
    PurchaseMethod,
    PurchaseMethodType,
    SerializedVariant,
} from '../types';
import './BuyingOptionsModal.css';

interface BuyingOptionsModalProps {
    open: boolean;
    onClose: () => void;
    product: ProductInfo;
    variant: SerializedVariant;
    shopId: number;
    resolvedCjUrl: string | undefined;
    onCjAttempted: () => void;
}

const RETAILER_LABEL: Record<PurchaseMethodType, string> = {
    shopify: 'Shopify',
    cj: 'CJ',
    awin: 'Awin',
    impact: 'Impact',
    avelon: 'Avelon',
    // No generic platform name for manual links; show the brand if present, else blank.
    manual_affiliate: '',
};

const AVAILABILITY_LABEL: Record<PurchaseAvailability, string> = {
    in_stock: 'in stock',
    out_of_stock: 'out of stock',
    preorder: 'preorder',
    backorder: 'backorder',
};

function getAvailabilityLabel(availability: PurchaseMethod['availability']) {
    if (!availability) { return AVAILABILITY_LABEL.in_stock; }
    return AVAILABILITY_LABEL[availability] ?? availability.replace(/_/g, ' ');
}

function getAvailabilityClass(availability: PurchaseMethod['availability']) {
    if (availability === 'out_of_stock') { return 'bom__stock bom__stock--out'; }
    if (availability === 'preorder' || availability === 'backorder') { return 'bom__stock bom__stock--limited'; }
    return 'bom__stock';
}

export function BuyingOptionsModal({
    open,
    onClose,
    product,
    variant,
    shopId,
    resolvedCjUrl,
    onCjAttempted,
}: BuyingOptionsModalProps) {
    const { addToCart, openCart } = useCart();
    const [addingShopify, setAddingShopify] = useState(false);
    const [pendingCjId, setPendingCjId] = useState<number | null>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);

    useBodyScrollLock(open);

    useEffect(() => {
        if (!open) { return; }
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') { onClose(); }
        };
        document.addEventListener('keydown', onKey);
        closeButtonRef.current?.focus();
        return () => {
            document.removeEventListener('keydown', onKey);
        };
    }, [open, onClose]);

    if (!open) { return null; }

    const onShopifyClick = async () => {
        if (addingShopify) { return; }
        setAddingShopify(true);
        try {
            const ok = await addToCart(variant.id, shopId);
            if (ok) {
                onClose();
                openCart();
            }
        } finally {
            setAddingShopify(false);
        }
    };

    const onCjRowClick = async (e: React.MouseEvent<HTMLAnchorElement>, pm: PurchaseMethod) => {
        if (resolvedCjUrl) { return; }
        e.preventDefault();
        if (pendingCjId !== null) { return; }
        const pmId = pm.id ?? 0;
        setPendingCjId(pmId);
        const url = await resolveCjLink(variant.id, shopId, pmId);
        setPendingCjId(null);
        onCjAttempted();
        const target = url ?? pm.link;
        if (target) {
            window.open(target, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <div className="bom" role="dialog" aria-modal="true" aria-labelledby="bom-title">
            <div className="bom__backdrop" onClick={onClose} />
            <div className="bom__panel">
                <div className="bom__product">
                    <div className="bom__image">
                        {variant.image_url && <img src={variant.image_url} alt={product.name} />}
                    </div>
                    <div className="bom__name">
                        {product.brand && <span className="bom__brand">{product.brand}</span>}
                        <span className="bom__product-name">{product.name}</span>
                    </div>
                </div>
                <div className="bom__content">
                    <div className="bom__header">
                        <div className="bom__header-text">
                            <h2 id="bom-title" className="bom__title">where to buy</h2>
                            <p className="bom__disclaimer">products may generate commissions</p>
                        </div>
                        <button
                            ref={closeButtonRef}
                            type="button"
                            className="bom__close"
                            onClick={onClose}
                            aria-label="Close buying options"
                        >
                            <CloseIcon className="bom__close-icon" />
                        </button>
                    </div>
                    <ul className="bom__items">
                        {variant.purchase_methods.map((pm, i) => {
                            const isShopify = pm.method_type === 'shopify';
                            const isCj = pm.method_type === 'cj';
                            const isManual = pm.method_type === 'manual_affiliate';
                            const shoppable = isShoppable(pm);
                            const retailerLabel = pm.retailer_name
                                ?? ((isShopify || isManual) && product.brand
                                ? product.brand
                                : RETAILER_LABEL[pm.method_type]);
                            const rowKey = pm.id ?? `${pm.method_type}-${i}`;

                            let href = pm.link ?? '#';
                            if (isCj && resolvedCjUrl) { href = resolvedCjUrl; }

                            const isPendingThisCj = isCj && pendingCjId === (pm.id ?? 0);
                            const buttonLabel = !shoppable
                                ? 'unavailable'
                                : isShopify
                                ? (addingShopify ? 'adding...' : 'add to cart')
                                : (isPendingThisCj ? 'opening...' : 'shop now');

                            return (
                                <li
                                    className={`bom__item${shoppable ? '' : ' bom__item--unavailable'}`}
                                    key={rowKey}
                                    style={{ '--bom-row-delay': `${360 + i * 90}ms` } as React.CSSProperties}
                                >
                                    <div className="bom__row">
                                        <span className="bom__retailer">{retailerLabel}</span>
                                        <div className="bom__pricing">
                                            {variant.price && <span className="bom__price">{variant.price}</span>}
                                            {!isManual && (
                                                <span className={getAvailabilityClass(pm.availability)}>
                                                    {getAvailabilityLabel(pm.availability)}
                                                </span>
                                            )}
                                        </div>
                                        {!shoppable ? (
                                            <Button
                                                label={buttonLabel}
                                                variant="primary"
                                                type="button"
                                                disabled
                                                className="bom__button bom__button--disabled"
                                            />
                                        ) : isShopify ? (
                                            <Button
                                                label={buttonLabel}
                                                variant="primary"
                                                type="button"
                                                onClick={onShopifyClick}
                                                className="bom__button"
                                            />
                                        ) : (
                                            <Button
                                                label={buttonLabel}
                                                variant="primary"
                                                href={href}
                                                target="_blank"
                                                rel="noopener noreferrer nofollow"
                                                onClick={isCj ? (e) => onCjRowClick(e, pm) : undefined}
                                                className="bom__button"
                                            />
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>
        </div>
    );
}
